// Credit:
//  - https://stackoverflow.com/a/30753870
const focusableSelector = [
  "[contentEditable=true]",
  '[tabindex]:not([tabindex="-1"])',
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "iframe",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
]

export function trapFocus(node: HTMLElement, listener: (event: KeyboardEvent) => void) {
  const modal = node.closest('[aria-modal="true"]') as HTMLElement
  if (modal) {
    requestAnimationFrame(() => modal.focus())  
  }
  node.addEventListener("keydown", listener)
}

export function releaseFocus(node: HTMLElement, listener: (event: KeyboardEvent) => void) {
  node.removeEventListener("keydown", listener)
}

/**
 * Finds all focusable elements in a container and its shadow DOM
 * @param container - The main container element
 * @param shadowRoot - The shadow root of the container (optional)
 * @returns An array of focusable elements
 */
export function findAllFocusableElements(container: HTMLElement, shadowRoot?: ShadowRoot | null): HTMLElement[] {
  const containerFocusable = Array.from(
    container.querySelectorAll(focusableSelector.join(","))
  ) as HTMLElement[];
  
  const shadowFocusable = shadowRoot
    ? Array.from(
        shadowRoot.querySelectorAll(focusableSelector.join(","))
      ) as HTMLElement[]
    : [];

  return [...containerFocusable, ...shadowFocusable];
}

/**
 * Checks if an element is in the main container or shadow DOM
 * @param container - The main container element
 * @param element - The element to check
 * @param shadowRoot - The shadow root of the container (optional)
 * @returns true if the element is in the container or shadow DOM, false otherwise
 */
export function isElementInScope(container: HTMLElement, element: Element | null, shadowRoot?: ShadowRoot | null): boolean {
  if (!element) return false;
  
  // Checks if element is in the main container
  if (container.contains(element)) return true;
  
  // Checks if element is in shadow DOM
  return shadowRoot?.contains?.(element) || false;
}

/**
 * Returns the deepest active element in the document or shadow root
 * @param doc - The document or shadow root to search in
 * @returns The deepest active element or null if no active element is found
 */
export function deepActiveElement(doc: Document | ShadowRoot = document): Element | null {
  const active = doc.activeElement;
  if (!active) return null;
  if (active.shadowRoot) {
    return deepActiveElement(active.shadowRoot);
  }
  return active;
}